import { useCallback, useEffect, useState } from 'react';
import { faHeart } from '@fortawesome/pro-solid-svg-icons/faHeart';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { generatePath, Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { SitterNoteModalNote } from 'us-react-components/dist/SitterNoteModal/SitterNoteModal';
import {
  UserNameFilter,
  UserNameFilterOption,
} from 'us-react-components/dist/UserNameFilter/UserNameFilter';
import {
  FavoriteSitter,
  FavoriteSitterService,
  PagedResponseMeta,
  ParentBlockSitterService,
  Sitter,
  SitterService,
} from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { Routes } from '../../Routes';
import { YourSittersCard } from './components/YourSittersCard';

type FilterType = 'bookmarked' | 'booked' | 'blocked';

const FILTERS: Array<{ key: FilterType; labelKey: string }> = [
  { key: 'bookmarked', labelKey: 'pages.yourSitters.filters.bookmarked' },
  { key: 'booked', labelKey: 'pages.yourSitters.filters.booked' },
  { key: 'blocked', labelKey: 'pages.yourSitters.filters.blocked' },
];

export const YourSitters = observer((): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { stores, services: { tracker } } = useDependencies();
  const user = stores.user.user;

  const parentId = pathId ? Number(pathId) : undefined;
  const pageParam = Number(searchParams.get('page') || 1) || 1;

  const initialFilter: FilterType = ((): FilterType => {
    if (searchParams.get('blocked')) return 'blocked';
    if (searchParams.get('booked')) return 'booked';
    return 'bookmarked';
  })();

  const [selectedType, setSelectedType] = useState<FilterType>(initialFilter);
  const [sitters, setSitters] = useState<Sitter[]>([]);
  const [meta, setMeta] = useState<PagedResponseMeta | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'UrbanSitter - Your Caregivers';
  }, []);

  useEffect(() => {
    if (!user?.id || !parentId) return;
    if (user.id !== parentId) {
      navigate(`/parent/${user.id}/your-sitters`, { replace: true });
    }
  }, [user?.id, parentId, navigate]);

  const nameFilterId = searchParams.get('sitterId');

  const fetchSitters = useCallback(
    async (userId: number, filter: FilterType, page: number, sitterIdFilter?: string | null): Promise<void> => {
      setLoading(true);
      try {
        const params: Record<string, unknown> = {
          parentId: userId,
          userId,
          page,
          active: true,
        };
        params[filter] = true;
        if (sitterIdFilter) params.sitterId = sitterIdFilter;
        const resp = (await SitterService.getByFilter({ params })).data;
        setSitters(resp.data);
        setMeta(resp.meta);
      } catch (err) {
        console.warn('[YourSitters] load failed', err);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!user?.id) return;
    fetchSitters(user.id, selectedType, pageParam, nameFilterId);
  }, [user?.id, selectedType, pageParam, nameFilterId, fetchSitters]);

  const onNameFilter = (u: UserNameFilterOption | null): void => {
    setSearchParams(
      (sp) => {
        const n = new URLSearchParams(sp);
        if (u?.id) n.set('sitterId', String(u.id));
        else n.delete('sitterId');
        n.set('page', '1');
        return n;
      },
      { replace: true },
    );
  };

  const setFilter = (next: FilterType): void => {
    setSearchParams((sp) => {
      const nextSp = new URLSearchParams(sp);
      // Drop old filter flags, set the new one.
      nextSp.delete('bookmarked');
      nextSp.delete('booked');
      nextSp.delete('blocked');
      nextSp.set(next, 'true');
      nextSp.set('page', '1');
      return nextSp;
    });
    setSelectedType(next);
  };

  const goToPage = (page: number): void => {
    setSearchParams((sp) => {
      const next = new URLSearchParams(sp);
      next.set('page', String(page));
      return next;
    });
  };

  const setFavorite = async (
    isFavorited: boolean,
    favoriteId: number,
    sitterId: number,
  ): Promise<void> => {
    if (!user?.id) return;
    const body: FavoriteSitter = { parentId: user.id, sitterId };
    if (isFavorited) {
      const resp = await FavoriteSitterService.create(body);
      const createdId = resp.data?.data?.id;
      setSitters((prev) =>
        prev.map((s) =>
          s.id === sitterId
            ? {
                ...s,
                relationship: {
                  ...(s.relationship ?? {}),
                  bookmarked: true,
                  favorite: { ...(s.relationship?.favorite ?? {}), id: createdId },
                },
              }
            : s,
        ),
      );
    } else {
      await FavoriteSitterService.delete(favoriteId);
      setSitters((prev) =>
        prev.map((s) =>
          s.id === sitterId
            ? {
                ...s,
                relationship: {
                  ...(s.relationship ?? {}),
                  bookmarked: false,
                  favorite: undefined,
                },
              }
            : s,
        ),
      );
    }
  };

  // Angular your-sitter-card.js:71 — toggleHidden(). If currently blocked,
  // DELETE /v2/blocked-sitters/:id; otherwise POST {parentId, sitterId}.
  const toggleHidden = async (sitter: Sitter): Promise<void> => {
    if (!user?.id || sitter.id === undefined) return;
    const current = sitter.relationship ?? {};
    // Angular stores the block record's id in relationship.blocked. React's
    // generated Relationship type narrows this to boolean; the wire value is
    // the numeric id when present, so treat it as `unknown`.
    const blockedId = (current as { blocked?: unknown }).blocked;
    try {
      if (typeof blockedId === 'number' && blockedId > 0) {
        await ParentBlockSitterService.delete(blockedId);
        setSitters((prev) =>
          prev.map((s) =>
            s.id === sitter.id
              ? { ...s, relationship: { ...(s.relationship ?? {}), blocked: undefined } }
              : s,
          ),
        );
        toast.success(
          t('pages.yourSitters.unhideSuccess', { name: sitter.firstName ?? '' }),
        );
      } else {
        const resp = await ParentBlockSitterService.create({
          parentId: user.id,
          sitterId: sitter.id,
        });
        const newId = (resp.data?.data as { id?: number } | undefined)?.id;
        setSitters((prev) =>
          prev.map((s) =>
            s.id === sitter.id
              ? {
                  ...s,
                  relationship: {
                    ...(s.relationship ?? {}),
                    blocked: newId as unknown as boolean,
                  },
                }
              : s,
          ),
        );
      }
    } catch {
      toast.error(t('pages.yourSitters.hideError'));
    }
  };

  const openSitterProfile = (sitter: Sitter): void => {
    if (sitter.id == null) return;
    navigate(generatePath(Routes.sitter.profile, { id: String(sitter.id) }));
  };

  const goToBooking = (sitter: Sitter): void => {
    if (!user?.id || !sitter.id) return;
    void tracker.track('your-sitters', {
      action: 'Request a Booking clicked',
      label: 'your sitters list',
      value: user.id,
    });
    const params = new URLSearchParams({
      userId: String(user.id),
      parentId: String(user.id),
      sitterId: String(sitter.id),
    });
    navigate(`${Routes.parent.bookSitter}?${params.toString()}`);
  };

  const onNoteSaved = (sitterId: number, note?: SitterNoteModalNote): void => {
    setSitters((prev) =>
      prev.map((s) =>
        s.id === sitterId
          ? {
              ...s,
              relationship: {
                ...(s.relationship ?? {}),
                parentNote: note
                  ? { id: note.id, comment: note.comment, updated: note.updated }
                  : undefined,
              },
            }
          : s,
      ),
    );
  };

  const parent = stores.parent.parent;
  const parentKidsCount = parent?.kids?.length ?? 1;

  const renderEmptyState = (): JSX.Element => {
    return (
      <div className='flex flex-col sm:flex-row items-center gap-6 bg-white border-2 border-sapphire-50 rounded-lg p-6'>
        <div className='relative w-24 h-24 rounded-full bg-neutral-100 flex-shrink-0 flex items-center justify-center'>
          {selectedType !== 'blocked' && (
            <FontAwesomeIcon icon={faHeart} className='text-3xl text-sapphire' />
          )}
        </div>
        <div className='flex-1 text-center sm:text-left'>
          {selectedType === 'bookmarked' && (
            <>
              <h4 className='text-lg font-semibold mb-2'>
                {t('pages.yourSitters.empty.bookmarked')}
              </h4>
              <p className='text-sm text-neutral-700'>
                <Button
                  buttonType={ButtonType.tertiary}
                  onClick={(): void => setFilter('booked')}
                  className='!p-0 !min-w-0 !text-sapphire underline'
                >
                  {t('pages.yourSitters.empty.bookmarkedPickFrom')}
                </Button>{' '}
                {t('pages.yourSitters.empty.bookmarkedAfterPick')}{' '}
                <Link to='/sitters' className='text-sapphire underline'>
                  {t('pages.yourSitters.empty.bookmarkedBrowse')}
                </Link>{' '}
                {t('pages.yourSitters.empty.bookmarkedEnd')}
              </p>
            </>
          )}
          {selectedType === 'blocked' && (
            <>
              <h4 className='text-lg font-semibold mb-2'>
                {t('pages.yourSitters.empty.blocked')}
              </h4>
            </>
          )}
          {selectedType === 'booked' && (
            <h4 className='text-lg font-semibold mb-2'>
              {t('pages.yourSitters.empty.booked')}
            </h4>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className='container py-8'>
      <h1 className='text-2xl font-semibold mb-2'>{t('pages.yourSitters.heading')}</h1>
      <p className='text-sm text-neutral-700 mb-6'>{t('pages.yourSitters.subheading')}</p>

      <div className='grid grid-cols-1 md:grid-cols-12 gap-4 mb-6'>
        <div className='md:col-span-4'>
          <label htmlFor='your-sitters-type' className='block text-sm font-semibold mb-1'>
            {t('pages.yourSitters.typeLabel', { defaultValue: 'Type' })}
          </label>
          <select
            id='your-sitters-type'
            className='form-control w-full border border-neutral-300 rounded px-3 py-2'
            value={selectedType}
            onChange={(e): void => setFilter(e.target.value as FilterType)}
            disabled={loading}
          >
            {FILTERS.map((f) => (
              <option key={f.key} value={f.key}>
                {t(f.labelKey)}
              </option>
            ))}
          </select>
        </div>
        <div className='md:col-span-4'>
          <UserNameFilter
            kind='sitter'
            viewerId={user?.id}
            label={t('common.caregiverLabel')}
            onSelect={onNameFilter}
          />
        </div>
      </div>

      {loading ? (
        <p className='text-neutral-600'>{t('common.loading')}</p>
      ) : sitters.length === 0 ? (
        renderEmptyState()
      ) : (
        <>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div className='flex flex-col'>
              {sitters
                .slice(0, Math.ceil(sitters.length / 2))
                .map((sitter) => (
                  <YourSittersCard
                    key={sitter.id}
                    sitter={sitter}
                    parentId={user?.id ?? 0}
                    parentKidsCount={parentKidsCount}
                    setFavorite={setFavorite}
                    onToggleHidden={toggleHidden}
                    onBook={goToBooking}
                    onOpenProfile={openSitterProfile}
                    onNoteSaved={onNoteSaved}
                  />
                ))}
            </div>
            <div className='flex flex-col'>
              {sitters
                .slice(Math.ceil(sitters.length / 2))
                .map((sitter) => (
                  <YourSittersCard
                    key={sitter.id}
                    sitter={sitter}
                    parentId={user?.id ?? 0}
                    parentKidsCount={parentKidsCount}
                    setFavorite={setFavorite}
                    onToggleHidden={toggleHidden}
                    onBook={goToBooking}
                    onOpenProfile={openSitterProfile}
                    onNoteSaved={onNoteSaved}
                  />
                ))}
            </div>
          </div>

          {selectedType === 'bookmarked' && sitters.length === 1 && (
            <div className='mt-4 flex flex-col sm:flex-row items-center gap-6 bg-white border-2 border-sapphire-50 rounded-lg p-6'>
              <div className='relative w-24 h-24 rounded-full bg-neutral-100 flex-shrink-0 flex items-center justify-center'>
                <FontAwesomeIcon icon={faHeart} className='text-3xl text-sapphire' />
              </div>
              <div className='flex-1 text-center sm:text-left'>
                <h4 className='text-lg font-semibold mb-2'>
                  {t('pages.yourSitters.lookingForMore')}
                </h4>
                <p className='text-sm'>
                  <Button
                    buttonType={ButtonType.tertiary}
                    onClick={(): void => setFilter('booked')}
                    className='!p-0 !min-w-0 !text-sapphire underline'
                  >
                    {t('pages.yourSitters.empty.bookmarkedPickFrom')}
                  </Button>{' '}
                  {t('pages.yourSitters.empty.bookmarkedAfterPick')}{' '}
                  <Link to='/sitters' className='text-sapphire underline'>
                    {t('pages.yourSitters.empty.bookmarkedBrowse')}
                  </Link>{' '}
                  {t('pages.yourSitters.empty.bookmarkedEnd')}
                </p>
              </div>
            </div>
          )}

          {meta && meta.pageCount !== undefined && meta.pageCount > 1 && (
            <div className='flex items-center justify-center gap-3 mt-6'>
              <Button
                buttonType={ButtonType.secondary}
                disabled={(meta.page ?? 1) <= 1}
                onClick={(): void => goToPage((meta.page ?? 1) - 1)}
              >
                {t('common.previous')}
              </Button>
              <span className='text-sm text-neutral-700'>
                {t('pages.yourSitters.pageOf', { current: meta.page, total: meta.pageCount })}
              </span>
              <Button
                buttonType={ButtonType.secondary}
                disabled={meta.lastPage === true || (meta.page ?? 1) >= (meta.pageCount ?? 1)}
                onClick={(): void => goToPage((meta.page ?? 1) + 1)}
              >
                {t('common.next')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
});
